<template>
  <b-modal
    :visible="visible"
    :title="modalTitle"
    size="xl"
    no-close-on-backdrop
    @hide="onHide"
  >
    <div v-if="apiError" class="alert alert-danger" role="alert">
      {{ apiError.message }}
      <span v-if="apiError.code" class="d-block small text-muted mt-1">{{ apiError.code }}</span>
    </div>

    <div v-if="detailLoading" class="text-muted py-4 text-center">
      <i class="fa fa-spinner fa-spin mr-1"></i> Loading Skill…
    </div>

    <template v-else>
      <!-- The state worth surfacing loudly, above everything else on the screen. A Skill
           with four drafts and nothing published looks busy and serves nobody, and
           neither the status pill nor the version count says which of those it is. -->
      <div v-if="health.message" class="skill-modal__health" :class="`is-${health.level}`">
        <i class="fa mr-1" :class="health.level === 'broken' ? 'fa-exclamation-circle' : 'fa-info-circle'"></i>
        {{ health.message }}
      </div>

      <!-- Something wrote to the row outside the application. The same check fails the
           user's message at send time (E1018-023); saying it here is what turns that
           into a warning an administrator sees first. -->
      <div v-if="tampered.length" class="alert alert-danger mt-3" role="alert">
        <strong>The stored text has been altered outside ARCH.</strong>
        {{ tamperedSentence }}
        Messages using it are refused at send time. Publish a fresh draft carrying the
        text you intend, and have engineering look at how the row was changed.
      </div>

      <!-- ── What the Skill is ──────────────────────────────────────────────────────
           Name, slug and description are the Skill's own identity and change without
           touching a word of its instructions — a rename is not a new version. -->
      <div class="form-row mt-3">
        <div class="col-md-4 form-group">
          <label class="skill-label" for="skill-name">
            NAME
            <i
              v-b-tooltip.hover
              class="fa fa-info-circle skill-info"
              title="What people see in the picker when they choose this Skill."
            ></i>
          </label>
          <input
            id="skill-name"
            v-model="form.name"
            type="text"
            class="form-control"
            :maxlength="MAX_NAME_CHARS"
            placeholder="Grill Me"
            @input="onNameInput"
          />
          <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name }}</div>
        </div>

        <div class="col-md-3 form-group">
          <label class="skill-label" for="skill-slug">
            SLUG
            <i
              v-b-tooltip.hover
              class="fa fa-info-circle skill-info"
              title="The Skill's stable identifier — lowercase, hyphenated, unique. Not shown to users."
            ></i>
          </label>
          <input
            id="skill-slug"
            v-model="form.slug"
            type="text"
            class="form-control"
            :maxlength="MAX_SLUG_CHARS"
            placeholder="grill-me"
            @input="slugTouched = true"
          />
          <div v-if="errors.slug" class="text-danger small mt-1">{{ errors.slug }}</div>
        </div>

        <div class="col-md-5 form-group">
          <label class="skill-label" for="skill-description">
            DESCRIPTION
            <i
              v-b-tooltip.hover
              class="fa fa-info-circle skill-info"
              title="One line under the name in the picker, saying what this Skill is for. Optional."
            ></i>
          </label>
          <input
            id="skill-description"
            v-model="form.description"
            type="text"
            class="form-control"
            :maxlength="MAX_DESCRIPTION_CHARS"
            placeholder="Stress-tests a plan by interviewing you about it"
          />
          <div v-if="errors.description" class="text-danger small mt-1">{{ errors.description }}</div>
        </div>
      </div>

      <!-- ── The instructions ───────────────────────────────────────────────────────
           Edited only as a draft, never in place. A published version is immutable and
           the backend refuses the edit outright, so there is no textarea here holding
           published text — the section shows what is live and offers a draft instead. -->
      <div class="skill-modal__section">
        <div class="skill-modal__section-head">
          <h6 class="skill-modal__section-title">
            Instructions
            <span v-if="draft" class="badge badge-secondary ml-2">draft v{{ draft.versionNumber }}</span>
          </h6>

          <span v-if="budget.chars" class="skill-modal__count" :class="`is-${budget.level}`">
            {{ formatNumber(budget.chars) }} characters · ~{{ formatNumber(budget.estimatedTokens) }} tokens
          </span>
        </div>

        <template v-if="editingBody">
          <textarea
            id="skill-body"
            v-model="form.body"
            class="form-control skill-modal__body"
            rows="14"
            placeholder="You are an assistant that…"
          ></textarea>

          <!-- The character cap is 100,000 and it is not the limit that matters. What
               matters is that every word here is charged against the context window
               before a single turn of the conversation, so an author who fills the cap
               finds out much later, in somebody else's chat. -->
          <div v-if="budget.message" class="skill-modal__budget" :class="`is-${budget.level}`">
            <i class="fa fa-exclamation-circle mr-1"></i>{{ budget.message }}
          </div>
        </template>

        <!-- No draft: the live text, read-only, and the only route to changing it. -->
        <template v-else>
          <div class="skill-modal__live">
            <div class="skill-modal__live-head">
              <span>
                <i class="fa fa-lock mr-1"></i>
                <template v-if="publishedVersionDetail">
                  Version {{ publishedVersionDetail.versionNumber }} is live and cannot be edited.
                </template>
                <template v-else>
                  Nothing is published yet.
                </template>
              </span>
              <b-button variant="primary" size="sm" :disabled="savingDraft" @click="startDraft">
                <i v-if="savingDraft" class="fa fa-spinner fa-spin mr-1"></i>
                <i v-else class="fa fa-pencil mr-1"></i>
                {{ publishedVersionDetail ? "Edit as a new draft" : "Write a draft" }}
              </b-button>
            </div>
            <pre v-if="publishedVersionDetail" class="skill-modal__live-body">{{ publishedVersionDetail.body }}</pre>
          </div>
        </template>
      </div>

      <!-- ── History ────────────────────────────────────────────────────────────────
           Kept in full because `ai_message.skill_version_id` points into it: a version
           nobody can see any more is a row in the audit trail nobody can read. -->
      <div v-if="!isCreate && versions.length" class="skill-modal__section">
        <h6 class="skill-modal__section-title mb-2">Version history</h6>

        <b-table
          responsive
          table-class="mb-0 table-custom border"
          head-variant="light"
          :items="versions"
          :fields="versionFields"
          striped
          small
        >
          <template #cell(versionNumber)="data">
            <span class="font-weight-bold">v{{ data.value }}</span>
            <i
              v-if="data.item.bodyIntact === false"
              v-b-tooltip.hover
              class="fa fa-exclamation-circle skill-danger ml-1"
              title="The stored text no longer matches the hash taken when this version was published. Messages using it are refused at send time."
            ></i>
          </template>

          <template #cell(status)="data">
            <span class="skill-pill" :class="`is-${data.value}`">{{ data.value }}</span>
          </template>

          <template #cell(publishedAt)="data">
            <span v-if="!data.value" class="text-muted">—</span>
            <span v-else v-b-tooltip.hover :title="absoluteTime(data.value)">
              {{ relativeTime(data.value) }}
            </span>
          </template>

          <template #cell(action)="data">
            <!-- Publish is the one control here that changes what users see, so it is
                 spelled out rather than reduced to an icon beside the others — and it
                 is absent, not disabled, for an administrator holding only
                 `ai.skill.manage`. -->
            <b-button
              v-if="actionsFor(data.item).canPublish"
              variant="success"
              size="sm"
              class="mr-2"
              :disabled="publishing || bodyDirty"
              @click="confirmPublish(data.item)"
            >
              <i v-if="publishing" class="fa fa-spinner fa-spin mr-1"></i>
              Publish
            </b-button>

            <b-button
              v-if="!actionsFor(data.item).isDraft"
              v-b-tooltip.hover
              variant="link"
              size="sm"
              class="p-0"
              :title="draft ? 'Copy this text into the open draft' : 'Start a new draft from this version'"
              :disabled="savingDraft"
              @click="copyToDraft(data.item)"
            >
              <i class="fa fa-copy"></i>
            </b-button>
          </template>
        </b-table>

        <!-- There is no re-publish, and the reason is worth stating where the copy icon
             is: history has to stay a record of what actually served messages. Absent
             until there is an earlier version, because until then it describes a route
             that does not exist. -->
        <p v-if="pastVersions.length" class="skill-modal__note">
          To go back to an earlier version, copy its text into a draft and publish that.
          Versions are never re-published, so the history stays a record of what actually
          served messages.
        </p>
      </div>
    </template>

    <template #modal-footer>
      <!-- Creating: the two endings of the job, in the order they are decided between.
           A new Skill is either finished and live or parked as a draft, and asking the
           author to create it and then find the Publish button to say so is a second
           step that carries no second decision. Publish is absent, not disabled, for an
           administrator holding only `ai.skill.manage`. -->
      <b-button
        v-if="isCreate && canPublish"
        variant="success"
        :disabled="saving || publishing"
        @click="confirmCreateAndPublish"
      >
        <i v-if="publishing" class="fa fa-spinner fa-spin mr-1"></i>
        Publish
      </b-button>

      <b-button
        :variant="isCreate && canPublish ? 'outline-primary' : 'primary'"
        :disabled="saving || publishing || detailLoading || (!isCreate && !isDirty)"
        @click="submit"
      >
        <i v-if="saving" class="fa fa-spinner fa-spin mr-1"></i>
        {{ isCreate ? "Create draft" : "Save changes" }}
      </b-button>

      <!-- Distinct from Save, and deliberately so: saving a draft affects nobody, and
           publishing takes effect on everyone's next message with no review in between. -->
      <template v-if="!isCreate && draft && canPublish">
        <b-button
          v-b-tooltip.hover
          variant="success"
          :disabled="publishing || bodyDirty"
          :title="bodyDirty ? 'Save the draft before publishing it.' : ''"
          @click="confirmPublish(draft)"
        >
          <i v-if="publishing" class="fa fa-spinner fa-spin mr-1"></i>
          Publish v{{ draft.versionNumber }}
        </b-button>
      </template>

      <!-- Archiving is how a Skill is retired; there is no delete, because the
           conversations that used it have to stay readable. Absent on the General
           Assistant, which the backend refuses to archive — it is the fallback for every
           new conversation, so archiving it would break the workspace for everyone. -->
      <b-button
        v-if="canArchive"
        variant="danger"
        :disabled="saving"
        @click="$emit('archive', detail)"
      >
        Archive
      </b-button>
      <b-button
        v-else-if="!isCreate && detail && detail.status === 'archived'"
        variant="outline-success"
        :disabled="saving"
        @click="$emit('restore', detail)"
      >
        Restore
      </b-button>

      <!-- "Close", not "Cancel", once the Skill exists: saving here leaves the modal
           open — this is a workspace with a version history in it, not a quick form —
           and a button labelled Cancel beside work already committed reads as an undo. -->
      <b-button variant="outline-secondary" @click="close">
        {{ isCreate ? "Cancel" : "Close" }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import moment from "moment";
import AiSkillAdminService from "@/services/aiSkillAdminService";
import { describeApiError } from "@/utils/ai/apiError";
import { changedFields, formatNumber } from "@/utils/ai/settings";
import {
  MAX_DESCRIPTION_CHARS,
  MAX_NAME_CHARS,
  MAX_SLUG_CHARS,
  currentDraft,
  describeBodyBudget,
  describeSkillHealth,
  describeVersionActions,
  slugify,
  tamperedVersions,
  validateSlug,
} from "@/utils/ai/skills";

/** The database rejected a second live version — another administrator got there first. */
const PUBLISH_RACE = "E1018-025";
/** The slug is taken. Belongs against the field, not in the banner above the form. */
const SLUG_TAKEN = "E1018-027";

export default {
  name: "SkillModal",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /** The row being edited, or null to create. Only its `id` is relied on. */
    skill: {
      type: Object,
      default: null,
    },
    /**
     * Whether the user holds `ai.skill.publish`.
     *
     * Publishing carries its own permission and the split is worth honouring here rather
     * than leaving to the 403: authoring is private and reversible, publishing is not.
     */
    canPublish: {
      type: Boolean,
      default: false,
    },
    /**
     * `ai_config.max_context_tokens`, so the length warning is measured against the
     * window this workspace actually has rather than a number invented in the client.
     */
    maxContextTokens: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      MAX_SLUG_CHARS,
      MAX_NAME_CHARS,
      MAX_DESCRIPTION_CHARS,

      /** The SkillDetailView, with every version and its body. Null while creating. */
      detail: null,
      detailLoading: false,

      form: emptyForm(),
      /** What the form held when it was loaded, so only what changed is ever sent. */
      baseline: emptyForm(),

      errors: {},
      apiError: null,
      saving: false,
      savingDraft: false,
      publishing: false,

      // A slug is proposed from the name only until the author types one of their own.
      // Rewriting a slug someone chose is worse than making them type it.
      slugTouched: false,

      versionFields: [
        { key: "versionNumber", label: "VERSION" },
        { key: "status", label: "STATUS" },
        { key: "publishedAt", label: "PUBLISHED" },
        { key: "action", label: "" },
      ],
    };
  },

  computed: {
    isCreate() {
      return !this.skill;
    },

    modalTitle() {
      if (this.isCreate) {
        return "New Skill";
      }
      // The detail once it has arrived, not the row we were handed: a rename saved
      // without closing would otherwise leave the old name in the title bar.
      const named = this.detail || this.skill;
      return named && named.name ? `Edit ${named.name}` : "Edit Skill";
    },

    versions() {
      return (this.detail && this.detail.versions) || [];
    },

    /** The draft being written, if there is one. At most one matters. */
    draft() {
      return currentDraft(this.detail);
    },

    /**
     * The versions that are actually history: published once, or archived since.
     *
     * A Skill whose only version is the draft being written has no earlier version to go
     * back to, and the note about rolling one back has nothing to describe.
     */
    pastVersions() {
      return this.versions.filter((version) => version.status !== "draft");
    },

    /** The live version with its body — the list view carries only a summary of it. */
    publishedVersionDetail() {
      return this.versions.find((version) => version.status === "published") || null;
    },

    /**
     * Whether there is a textarea at all.
     *
     * Creating, or a draft exists. Never for published text: the edit control is absent
     * rather than disabled, because the immutability is the design and a greyed-out box
     * says "not now" when the truth is "not ever".
     */
    editingBody() {
      return this.isCreate || Boolean(this.draft);
    },

    budget() {
      return describeBodyBudget(this.form.body, this.maxContextTokens);
    },

    health() {
      return this.isCreate ? { level: "new", message: null } : describeSkillHealth(this.detail);
    },

    tampered() {
      return tamperedVersions(this.detail);
    },

    tamperedSentence() {
      const names = this.tampered.map((version) => `v${version.versionNumber}`);
      return names.length === 1
        ? `Version ${names[0]} no longer matches the hash recorded when it was published.`
        : `Versions ${names.join(", ")} no longer match the hashes recorded when they were published.`;
    },

    /** Details changed — a rename, a re-slug, a new description. One PATCH. */
    detailChanges() {
      return changedFields(
        {
          slug: this.baseline.slug,
          name: this.baseline.name,
          // Normalised on both sides. Otherwise an untouched empty description reads as
          // "" against null and a form nobody has typed in reports itself as dirty —
          // which puts a discard-your-changes question in front of a plain Cancel.
          description: this.baseline.description || null,
        },
        {
          slug: this.form.slug.trim(),
          name: this.form.name.trim(),
          // Empty means no description, which is a real value the column takes.
          description: this.form.description.trim() || null,
        },
      );
    },

    bodyDirty() {
      return this.editingBody && this.form.body !== this.baseline.body;
    },

    isDirty() {
      return Object.keys(this.detailChanges).length > 0 || this.bodyDirty;
    },

    /**
     * The General Assistant is checked on archive, not on edit.
     *
     * Its text and its name are freely editable; only the archive control goes, because
     * it is the fallback for every new conversation and the administrator who archived
     * it would have no way to know what they had broken.
     */
    canArchive() {
      return Boolean(this.detail) && !this.detail.isProtected && this.detail.status !== "archived";
    },
  },

  watch: {
    // Reset on open rather than on close: a failed save leaves the modal up holding the
    // values that failed, which is what you want in order to correct them.
    visible(open) {
      if (open) {
        this.reset();
      }
    },
  },

  methods: {
    formatNumber,

    relativeTime(value) {
      return value ? moment(value).fromNow() : "";
    },

    absoluteTime(value) {
      return value ? moment(value).format("D MMM YYYY, HH:mm") : "";
    },

    actionsFor(version) {
      return describeVersionActions(version, this.canPublish);
    },

    reset() {
      this.errors = {};
      this.apiError = null;
      this.saving = false;
      this.savingDraft = false;
      this.publishing = false;
      this.slugTouched = false;
      this.detail = null;
      this.form = emptyForm();
      this.baseline = emptyForm();

      if (!this.isCreate) {
        this.loadDetail();
      }
    },

    /**
     * The detail view, fetched on open rather than working from the row we were handed.
     *
     * The list carries no bodies and only a summary of the published version, and every
     * decision in this modal is about a body.
     */
    async loadDetail() {
      this.detailLoading = true;
      try {
        const response = await AiSkillAdminService.getSkill(this.skill.id);
        this.applyDetail(response.data);
      } catch (error) {
        this.apiError = describeApiError(error, "That Skill could not be loaded.");
      } finally {
        this.detailLoading = false;
      }
    },

    /**
     * Take a fresh detail view as the truth, and rebuild the form and its baseline.
     *
     * Called after every write, including a publish — publishing moves the draft to
     * published and archives what was live, so a form still holding the old draft would
     * be offering to edit a version that is now immutable.
     */
    applyDetail(detail) {
      this.detail = detail;

      const draft = currentDraft(detail);
      const loaded = {
        slug: detail.slug,
        name: detail.name,
        description: detail.description || "",
        body: draft ? draft.body : "",
      };

      this.form = { ...loaded };
      this.baseline = {
        ...loaded,
        // Null rather than "" so an absent description does not read as a change.
        description: detail.description || null,
      };
    },

    onNameInput() {
      if (this.isCreate && !this.slugTouched) {
        this.form.slug = slugify(this.form.name);
      }
    },

    /**
     * The × and Escape, which close the modal from inside it.
     *
     * `visible` is a one-way prop, so BootstrapVue hides itself on those and leaves the
     * parent still holding `true` — the modal would then be shut with no way to reopen
     * it. Intercepting `hide` keeps the two in step, and gives the unsaved-work question
     * somewhere to be asked from.
     *
     * `trigger` is null when the hide came from the parent setting `visible` to false,
     * which is the tail of a close this component already agreed to.
     */
    onHide(bvEvent) {
      if (!bvEvent.trigger) {
        return;
      }

      if (!this.isDirty || this.detailLoading) {
        this.$emit("close");
        return;
      }

      bvEvent.preventDefault();
      this.confirmDiscard();
    },

    close() {
      if (!this.isDirty || this.detailLoading) {
        this.$emit("close");
        return;
      }

      this.confirmDiscard();
    },

    /**
     * Closing with unsaved edits asks first.
     *
     * The instructions box holds work that exists nowhere else until it is saved — a
     * draft body is not recoverable from anything on the server — so the one click that
     * throws it away is worth a sentence.
     */
    confirmDiscard() {
      this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-danger btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: "Discard unsaved changes?",
          text: "The edits made here have not been saved and will be lost.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Discard",
          cancelButtonText: "Keep editing",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.$emit("close");
          }
        });
    },

    /**
     * The same bounds the backend validates, so a typo comes back as a sentence beside
     * the field rather than a 400 carrying a class-validator message.
     */
    validate() {
      const errors = {};
      const form = this.form;

      const slugError = validateSlug(form.slug);
      if (slugError) {
        errors.slug = slugError;
      }

      if (!form.name.trim()) {
        errors.name = "A name is required.";
      } else if (form.name.length > MAX_NAME_CHARS) {
        errors.name = `${MAX_NAME_CHARS} characters at most.`;
      }

      if (form.description.length > MAX_DESCRIPTION_CHARS) {
        errors.description = `${MAX_DESCRIPTION_CHARS} characters at most.`;
      }

      // Only when there is a body in play. Renaming a Skill whose live text is published
      // has no textarea and nothing to validate. The sentence itself is already under the
      // field — `budget.message` renders it — so this only has to stop the submit.
      if (this.editingBody && (this.budget.level === "empty" || this.budget.level === "over-cap")) {
        errors.body = this.budget.message;
      }

      this.errors = errors;
      return !Object.keys(errors).length;
    },

    async submit() {
      if (!this.validate()) {
        return;
      }

      this.saving = true;
      this.apiError = null;

      try {
        if (this.isCreate) {
          await this.create();
          return;
        }
        await this.saveExisting();
      } catch (error) {
        this.reportError(error, "That Skill could not be saved.");
      } finally {
        this.saving = false;
      }
    },

    async create() {
      // The Skill and its first draft in one request: a Skill with no version can be
      // neither published nor selected, so creating them separately only ever produces
      // a row in that state.
      const response = await AiSkillAdminService.createSkill({
        slug: this.form.slug.trim(),
        name: this.form.name.trim(),
        description: this.form.description.trim() || null,
        body: this.form.body,
      });

      this.$emit("saved", { skill: response.data, action: "created" });
    },

    /**
     * Publishing straight from the create form.
     *
     * The same confirmation the Publish button in an existing Skill asks for, minus the
     * sentence about archiving what was live: there is nothing live yet. What is worth
     * saying is the part that is true either way — this reaches users with no review
     * step between the click and someone reading the result.
     */
    confirmCreateAndPublish() {
      if (!this.validate()) {
        return;
      }

      this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-primary btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: `Publish "${this.form.name.trim()}"?`,
          text:
            "It is created and made live in one step: it appears in the picker and serves " +
            "messages that select it, with no review in between. Create it as a draft " +
            "instead to keep it out of the picker until you are ready.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Publish",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.createAndPublish();
          }
        });
    },

    /**
     * Create, then publish the first version.
     *
     * Two requests, because the create endpoint has no publish flag — a new Skill is
     * always a draft. That leaves one state worth handling honestly: the create lands
     * and the publish does not, whether because the grant is missing or because the row
     * was refused. The Skill exists at that point, so the modal closes and reports it as
     * the draft it actually is rather than staying on a form whose Create button would
     * now collide with its own slug.
     */
    async createAndPublish() {
      this.publishing = true;
      this.apiError = null;

      let created = null;
      try {
        const response = await AiSkillAdminService.createSkill({
          slug: this.form.slug.trim(),
          name: this.form.name.trim(),
          description: this.form.description.trim() || null,
          body: this.form.body,
        });
        created = response.data;
      } catch (error) {
        this.reportError(error, "That Skill could not be created.");
        this.publishing = false;
        return;
      }

      const version = currentDraft(created);
      if (!version) {
        this.publishing = false;
        this.$emit("saved", { skill: created, action: "created" });
        return;
      }

      try {
        const published = await AiSkillAdminService.publishVersion(created.id, version.id);
        this.$emit("saved", {
          skill: published.data,
          action: "published",
          versionNumber: version.versionNumber,
        });
      } catch (error) {
        this.$emit("saved", {
          skill: created,
          action: "created",
          publishError: describeApiError(error, "It could not be published.").message,
        });
      } finally {
        this.publishing = false;
      }
    },

    /**
     * Two endpoints, one button.
     *
     * The identity and the instructions are genuinely separate writes — a rename is not
     * a new version — but from the author's side there is one set of changes in front of
     * them, and a form with two Save buttons invites saving half of them.
     */
    async saveExisting() {
      const changes = this.detailChanges;
      let detail = this.detail;

      if (Object.keys(changes).length) {
        const response = await AiSkillAdminService.updateSkill(this.detail.id, changes);
        detail = response.data;
      }

      if (this.bodyDirty) {
        if (this.draft) {
          await AiSkillAdminService.updateVersion(this.detail.id, this.draft.id, this.form.body);
        } else {
          await AiSkillAdminService.createVersion(this.detail.id, this.form.body);
        }
        // The version write returns the version, not the Skill, and the version list has
        // moved — so the Skill is re-read rather than patched together here.
        const refreshed = await AiSkillAdminService.getSkill(this.detail.id);
        detail = refreshed.data;
      }

      this.applyDetail(detail);
      this.$emit("saved", { skill: detail, action: "saved", keepOpen: true });
    },

    /**
     * Start a draft, empty or carrying an existing version's text.
     *
     * This is also the rollback: there is no re-publish, because a published version is
     * immutable and an archived one stays archived. Going back means a new version that
     * carries the old body, so `ai_message.skill_version_id` keeps pointing at whatever
     * actually served each message.
     */
    async startDraft() {
      const seed = this.publishedVersionDetail ? this.publishedVersionDetail.body : "";
      await this.openDraftWith(seed);
    },

    async copyToDraft(version) {
      await this.openDraftWith(version.body);
    },

    /**
     * Put this text into a draft — the open one if there is one, a new one otherwise.
     *
     * Reusing the open draft rather than adding a second is deliberate: two drafts on
     * one Skill leaves the author guessing which of them Publish is about to send.
     */
    async openDraftWith(body) {
      this.savingDraft = true;
      this.apiError = null;

      try {
        if (this.draft) {
          await AiSkillAdminService.updateVersion(this.detail.id, this.draft.id, body);
        } else {
          await AiSkillAdminService.createVersion(this.detail.id, body);
        }

        const refreshed = await AiSkillAdminService.getSkill(this.detail.id);
        this.applyDetail(refreshed.data);
        this.$emit("saved", { skill: refreshed.data, action: "draft", keepOpen: true });
      } catch (error) {
        this.reportError(error, "That draft could not be started.");
      } finally {
        this.savingDraft = false;
      }
    },

    /**
     * Ask before publishing, and say what it does.
     *
     * There is no review step between this click and a user reading the result, and the
     * version currently live is archived by the same transaction. Neither of those is
     * visible from a button labelled "Publish".
     */
    confirmPublish(version) {
      const live = this.publishedVersionDetail;
      const willArchive = live
        ? ` Version ${live.versionNumber} is archived at the same moment.`
        : "";
      const staysArchived =
        this.detail && this.detail.status === "archived"
          ? " This Skill is archived, so publishing does not put it back in the picker — restore it separately."
          : "";

      this.$swal
        .mixin({
          customClass: {
            confirmButton: "btn btn-primary btn-lg my-3",
            cancelButton: "btn btn-outline-secondary btn-lg",
          },
          buttonsStyling: false,
        })
        .fire({
          title: `Publish version ${version.versionNumber}?`,
          text:
            "This becomes the live text for everyone's next message, with no review step in " +
            `between.${willArchive}${staysArchived}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Publish",
          cancelButtonText: "Cancel",
        })
        .then((result) => {
          if (result.isConfirmed) {
            this.publish(version);
          }
        });
    },

    async publish(version) {
      this.publishing = true;
      this.apiError = null;

      try {
        const response = await AiSkillAdminService.publishVersion(this.detail.id, version.id);
        this.applyDetail(response.data);
        this.$emit("saved", {
          skill: response.data,
          action: "published",
          versionNumber: version.versionNumber,
          keepOpen: true,
        });
      } catch (error) {
        const described = describeApiError(error, "That version could not be published.");

        // Another administrator published in the same instant and the unique index
        // refused the second one. Showing the raw conflict would leave this screen
        // displaying a draft that is no longer publishable; re-reading it shows what is
        // actually live, which is the thing to look at before trying again.
        if (described.code === PUBLISH_RACE) {
          await this.loadDetail();
          this.apiError = {
            message:
              "Another administrator published a version at the same moment, so this one was " +
              "refused. What is live now is shown below — check it before publishing again.",
            code: described.code,
          };
        } else {
          this.apiError = described;
        }
      } finally {
        this.publishing = false;
      }
    },

    /** A refused slug belongs against the slug field, not in the banner above the form. */
    reportError(error, fallback) {
      const described = describeApiError(error, fallback);

      if (described.code === SLUG_TAKEN) {
        this.$set(this.errors, "slug", "That slug is already in use by another Skill.");
        return;
      }

      this.apiError = described;
    },
  },
};

function emptyForm() {
  return {
    slug: "",
    name: "",
    description: "",
    body: "",
  };
}
</script>

<style scoped>
.skill-label {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: #5d6474;
}

.skill-info {
  margin-left: 4px;
  color: #a8afbd;
  cursor: help;
}

.skill-info:hover {
  color: #6eb644;
}

.skill-danger {
  color: #dc3545;
  cursor: help;
}

.skill-modal__health {
  padding: 9px 12px;
  border-radius: 8px;
  font-size: 13px;
}

/* Red only for the one that means nobody can use this Skill. Retired and unlisted are
   decisions someone made, and painting those as alarms teaches the eye to skip all
   three. */
.skill-modal__health.is-broken {
  border: 1px solid #f2b8bd;
  background: #fdeced;
  color: #b02a37;
}

.skill-modal__health.is-retired,
.skill-modal__health.is-unlisted {
  border: 1px solid #e6e9ed;
  background: #f5f7fa;
  color: #5d6474;
}

.skill-modal__section {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #eef0f4;
}

.skill-modal__section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.skill-modal__section-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5d6474;
}

.skill-modal__count {
  font-size: 12px;
  color: #8a91a0;
}

.skill-modal__count.is-crowding {
  color: #8a5a1a;
}

.skill-modal__count.is-oversized,
.skill-modal__count.is-over-cap {
  color: #b02a37;
}

.skill-modal__body {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.55;
  resize: vertical;
}

.skill-modal__budget {
  margin-top: 8px;
  padding: 8px 11px;
  border-radius: 8px;
  font-size: 12px;
}

.skill-modal__budget.is-crowding {
  border: 1px solid #f0d6b8;
  background: #fdf6ec;
  color: #8a5a1a;
}

.skill-modal__budget.is-oversized,
.skill-modal__budget.is-over-cap,
.skill-modal__budget.is-empty {
  border: 1px solid #f2b8bd;
  background: #fdeced;
  color: #b02a37;
}

.skill-modal__live {
  border: 1px solid #e6e9ed;
  border-radius: 8px;
  background: #f9fafc;
}

.skill-modal__live-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  font-size: 13px;
  color: #5d6474;
}

.skill-modal__live-body {
  max-height: 280px;
  margin: 0;
  padding: 12px;
  overflow: auto;
  border-top: 1px solid #e6e9ed;
  background: transparent;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 13px;
  line-height: 1.55;
  color: #2b3243;
  white-space: pre-wrap;
  word-break: break-word;
}

.skill-modal__note {
  margin: 10px 0 0;
  font-size: 12px;
  color: #8a91a0;
}

.skill-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.skill-pill.is-published {
  background: rgba(110, 182, 68, 0.14);
  color: #4a7f2c;
}

.skill-pill.is-draft {
  background: #eef1f6;
  color: #5d6474;
}

.skill-pill.is-archived {
  background: #f2f4f8;
  color: #8a91a0;
}
</style>
